import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type GridApi, type GridReadyEvent, type CellDoubleClickedEvent, type IRowNode } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Button, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Select } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

import "./audit_manage_detail.scoped.scss";

import AuditManagePopup from "@user/views/stock/popup_audit_manage";

interface State {
  gridApi: GridApi | null,
  stockCheckMngNo: string,
  actNecYn: string,
  workStationId: string,
  stockCheckDetailNo: string,
  isShowAuditManage: boolean
}

const defState = {
  gridApi: null,
  stockCheckMngNo: "",
  actNecYn: "Y",
  workStationId: "",
  stockCheckDetailNo: "",
  isShowAuditManage: false
};

interface SearchParams {
  export: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  export: "",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Info {
  stockCheckMngNo: string,
  detailInfos: any[],
  equipWorkInfos: any[]
}

const defInfo = {
  stockCheckMngNo: "",
  detailInfos: [],
  equipWorkInfos: []
};

const validationMsg4reIn = [
  {id: "skuNo", type: "NotFound", msg: "재입고할 SKU가 없습니다."},
  {id: "workStationId", type: "NotBlank", msg: "작업대를 선택해 주세요."}
];

const validationMsg4reqSkuPick = [
  {id: "reqInfos", type: "NotBlank", msg: "지정출고 SKU를 선택해 주세요."},
  {id: "workStationId", type: "NotBlank", msg: "작업대를 선택해 주세요."},
  {id: "skuNo", type: "InvalidStatus", msg: "현재 작업대에서 호출할 수 없는 SKU입니다."}
];

const defColDef: CustomColDef = {
  resizable: true,
  sortable: true,
  cellStyle: (p) => ({
    textAlign: p.colDef.headerClass == "hd-ta-right" ? "right" : p.colDef.headerClass == "hd-ta-left" ? "left" : "center",
    cursor: (p.data.diffQty != null && p.data.diffQty != "" && Number(p.data.diffQty) !== 0) ? "pointer" : "default"
  })
};

function AuditManageDetail(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const isActiveTab = useTabsStore((state) => state.currTabId == props.id);
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);
  const gridState4equip = useMemo(() => useSettingsStore.getState().getGridState(location.path + ".equip"), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Location", field: "locationId", width: 120, valueFormatter: valueFormatter.locationId },
    { headerName: "SKU No", field: "skuNoEx", width: 100 },
    { headerName: "관리구분", field: "mngType", width: 100, valueFormatter: valueFormatter.commonCode("mngType") },
    { headerName: "창고", field: "wh", width: 100, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 150 },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150 },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150 },
    { headerName: "KWD", headerClass: "hd-ta-left", field: "kwd", width: 150 },
    { headerName: "CTG", headerClass: "hd-ta-left", field: "ctg", width: 80 },
    { headerName: "Stock Qty", headerClass: "hd-ta-right", field: "stockQty", width: 110, valueFormatter: valueFormatter.number },
    { headerName: "Check Qty", headerClass: "hd-ta-right", field: "checkQty", width: 110, valueFormatter: valueFormatter.number },
    { headerName: "Diff Qty", headerClass: "hd-ta-right", field: "diffQty", width: 110, valueFormatter: valueFormatter.number },
    { headerName: "UOM", field: "uom", width: 80 },
    { headerName: "실사자", field: "checkUserName", width: 100 },
    { headerName: "조치자", field: "actUserName", width: 100,
      valueGetter: (p: any) => {
        return (p.data.diffQty != null && p.data.diffQty != "" && Number(p.data.diffQty) !== 0 ? p.data.actUserName : "-");
      }
    },
    { headerName: "조치상태", field: "actStatus", width: 100,
      valueGetter: (p: any) => {
        return (p.data.diffQty != null && p.data.diffQty != "" && Number(p.data.diffQty) !== 0 ? commonCode.getCodeValue("actStatus", p.data.actStatus) : "-");
      }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridOptions = useMemo(() => ({
    rowSelection: {
      mode: "multiRow" as const,
      isRowSelectable: (n: IRowNode) => {
        return (
          (n.data.diffQty != null && n.data.diffQty != "" && Number(n.data.diffQty) !== 0 && n.data.outPossYn == "Y")
        );
      },
      hideDisabledCheckboxes: true
    },
    selectionColumnDef: {
      resizable: true
    }
  }), []);
  const gridCallbacks = useStableHandlers({
    onGridReady: (e: GridReadyEvent) => {
      setState(prev => ({ ...prev, gridApi: e.api }));
    },
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onCellDoubleClicked: (e: CellDoubleClickedEvent) => {
      if (e.column.getColId() === "ag-Grid-SelectionColumn") return;
      if (e.data.diffQty != null && e.data.diffQty != "" && Number(e.data.diffQty) !== 0) {
        openPopupAuditManage(e.data.stockCheckDetailNo);
      }
    },
  });

  const gridInfo4equip = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "Track No", field: "workNoMfc", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업구분", field: "workType", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workType") },
    { headerName: "창고", field: "wh", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("wh") },
    { headerName: "P/N", headerClass: "hd-ta-left", field: "pn", width: 160, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "TR No", headerClass: "hd-ta-left", field: "trNo", width: 150, cellStyle: { textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "S/N", headerClass: "hd-ta-left", field: "sn", width: 150, cellStyle: {textAlign: "left"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Job Qty", headerClass: "hd-ta-right", field: "jobQty", width: 100, cellStyle: {textAlign: "right"}, valueFormatter: valueFormatter.number, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "작업대", field: "workStationId", width: 80, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workStationId") },
    { headerName: "SKU No", field: "skuNoEx", width: 100, cellStyle: {textAlign: "center"}, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "Location", field: "startPos", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.locationId, filter: true, filterParams: {buttons: ["reset"]} },
    { headerName: "설비", field: "workEquip", width: 100, cellStyle: {textAlign: "center"}, valueFormatter: valueFormatter.commonCode("workEquip") },
    { headerName: "설비상태", field: "workStatusEquip", width: 100, cellStyle: {textAlign: "center"},
      valueGetter: (p: any) => {
        return (p.data.workStatusEquip == "START" || p.data.workStatusEquip == "ACTIVE" ? p.data.currPos : commonCode.getCodeValue("workStatusEquip", p.data.workStatusEquip));
      }
    }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks4equip = {
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path + ".equip");
    }
  };

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  // 바코드 스캔 기능 적용
  const onBarcodeScanRef = useRef(onBarcodeScan);
  useEffect(() => {
    onBarcodeScanRef.current = onBarcodeScan;
  });

  useEffect(() => {
    if (!isActiveTab || state.isShowAuditManage) {
      return;
    }

    const handler = (code: string) => onBarcodeScanRef.current(code);
    kutil.startBarcodeScan(handler);
    return () => kutil.stopBarcodeScan(handler);
  }, [isActiveTab, state.isShowAuditManage]);
  // 끝: 바코드 스캔 기능 적용

  // 변경 작업대ID 저장
  useEffect(() => {
    if (state.workStationId) {
      useSettingsStore.getState().setLocalSetting("workStationId", location.path, state.workStationId);
    }
  }, [state.workStationId]);

  const firstSearch = useRef(true);
  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return;
    }
    searchInfo();
  }, [state.stockCheckMngNo, state.actNecYn, state.workStationId]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const query = Object.fromEntries(new URLSearchParams(location.search).entries());
    state.stockCheckMngNo = query.stockCheckMngNo || "";

    if (!state.workStationId) {
      const workStationId = useSettingsStore.getState().getDefWorkStationId(location.path)
        || useSettingsStore.getState().getWorkStationId(location.path)
        || (commonCode.workStationId as any[])?.[0]?.code
        || "";
      state.workStationId = workStationId ;
    }

    setState({ ...state });
  }

  async function searchInfo() {
    if (!state.stockCheckMngNo) {
      return;
    }

    try {
      const data = await kutil.sendGet("/api/stock/audit_manage_detail.data?mode=search", {stockCheckMngNo: state.stockCheckMngNo, actNecYn: state.actNecYn, workStationId: state.workStationId}, {});
      setInfo({
        ...data.info,
        detailInfos: data.detailInfos,
        equipWorkInfos: data.equipWorkInfos,
      });
    } catch(e) {
      console.log(e);
    }
  }
 
  async function reqSkuPick() {
    const selectedInfos = state.gridApi!.getSelectedRows();
    if (selectedInfos.length == 0) {
      kutil.alert("지정출고 SKU를 선택해 주세요.");
      return;
    }

    if (!state.workStationId) {
      kutil.alert("작업대를 선택해 주세요.");
      return;
    }

    for (const selectedInfo of selectedInfos) {
      selectedInfo.workStationId = state.workStationId;
    }

    if (await kutil.confirm("지정출고하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/audit_manage_detail.proc?mode=reqSkuPick", selectedInfos, {
        validationMsg: validationMsg4reqSkuPick
      });
      await kutil.alert("지정출고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function reIn() {
    if (await kutil.confirm("재입고하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/stock/audit_manage_detail.proc?mode=reIn", {workStationId: state.workStationId}, {
        validationMsg: validationMsg4reIn
      });
      await kutil.alert("재입고하였습니다.");
      router.reload();
    } catch(e) {
      console.log(e);
    }
  }

  async function downloadExcel() {
    const searchParams = kutil.copyObj({stockCheckMngNo: state.stockCheckMngNo, actNecYn: state.actNecYn, workStationId: state.workStationId}, {} as SearchParams);
    searchParams.export = "excel";
    searchParams.page = 1;
    searchParams.pageSize = 10000;
    try {
      await kutil.downloadFile("/api/stock/audit_manage_detail.download?mode=search", searchParams, {
        "fileName": "재고실사조치상세_" + kutil.date2string(kutil.getCurrLocalDate(), "yyyyMMddHHmmss") + ".xlsx"
      });
    } catch(e) {
      console.log(e);
    }
  }

  function goList() {
    router.navigateByPath("/stock/audit_manage_list", JSON.parse(decodeURI(router.query("searchParams") || "{}")));
  }

  async function openPopupAuditManage(stockCheckDetailNo: string) {
    setState(prev => ({ ...prev, isShowAuditManage: true, stockCheckDetailNo: stockCheckDetailNo }));
  }

  async function closePopupAuditManage(data: any) {
    setState(prev => ({ ...prev, isShowAuditManage: false }));

    if (data?.isSuccess) {
      router.reload();
    }
  }

  async function onBarcodeScan(code: string) {
    const [prefix, ...rest] = code.split(":");
    const value = rest.join(":").trim();

    if (rest.length > 0) {
      switch (prefix) {
        case "WS": {
          if (!value) return;
          const exists = (commonCode.workStationId as any[]).some(c => c.code === value);
          if (!exists) {
            kutil.alert("일치하는 작업대가 없습니다.\r\n" + code);
            return;
          }
          setState(prev => ({ ...prev, workStationId: value }));
          return;
        }
        default:
          return;
      }
    }
  }

  return (
    <>
      {/* contents */}
      <div className="contents">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>재고실사 조치</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={goList}>목록</Button>
            { auth.download &&
            <Button variant="outlined" onClick={downloadExcel}>엑셀다운</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={reIn}>재입고</Button>
            }
            { auth.write &&
            <Button variant="outlined" onClick={reqSkuPick}>지정출고</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 그리드 상단 */}
        <div className="grid-top-area">
          <div>
            <div className="form-base">
              <FormControl>
                <RadioGroup
                  row
                  value={state.actNecYn}
                  onChange={(e) => setState(prev => ({ ...prev, actNecYn: e.target.value }))}
                >
                  <FormControlLabel value="" control={<Radio size="small" />} label="전체" />
                  <FormControlLabel value="Y" control={<Radio size="small" />} label="조치필요" />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
        </div>
        {/*// 그리드 상단 */}
        {/* 실사 정보 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={info.detailInfos}
              defaultColDef={defColDef}
              initialState={gridState}
              rowSelection={gridOptions.rowSelection}
              selectionColumnDef={gridOptions.selectionColumnDef}
              showInfo={false}
              onGridReady={gridCallbacks.onGridReady}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onCellDoubleClicked={gridCallbacks.onCellDoubleClicked}
            />
          </div>
        </AgGridProvider>
        {/*// 실사 정보 */}
        {/* 서브 타이틀 */}
        <div className="sub-title-area">
          <p>작업대 작업 목록</p>
          <div className="workstation">
            <p>작업대</p>
            <FormControl size="small">
              <Select
                value={state.workStationId}
                onChange={(e) => {setState(prev => ({ ...prev, workStationId: e.target.value }))}}
                displayEmpty
              >
              {(commonCode.workStationId as any[]).map((code) => (
                <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
              ))}
              </Select>
            </FormControl>
          </div>
        </div>
        {/* 작업 목록 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo4equip}
              rowData={info.equipWorkInfos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState4equip}
              showInfo={false}
              onStateUpdated={gridCallbacks4equip.onStateUpdated}
            />
          </div>
        </AgGridProvider>
        {/*// 작업 목록 */}
        {state.isShowAuditManage && <AuditManagePopup isShow={state.isShowAuditManage} onClose={closePopupAuditManage} id={props.id} data={{stockCheckDetailNo: state.stockCheckDetailNo}} />}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(AuditManageDetail);
